import { Box } from "@mui/material";
import { type FC, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssistantAvatar } from "../UIToolkit/AssistantAvatar/AssistantAvatar";
import { AssistantAvatarMood } from "../UIToolkit/AssistantAvatar/assistantAvatarConfig";
import { type GazeDirection, gazeToward } from "../UIToolkit/AssistantAvatar/assistantAvatarGeometry";
import { ChatAvatarRow } from "../UIToolkit/Chat/ChatAvatarRow";
import { ChatThinkingIndicator } from "../UIToolkit/Chat/ChatThinkingIndicator";
import { ChatWorkSteps } from "../UIToolkit/Chat/ChatWorkSteps";
import { CHAT_WORK_STEP_ACTIVE_SELECTOR, ChatWorkStepsMode } from "../UIToolkit/Chat/chatWorkStepTypes";
import { ANNIE_TURN_AVATAR_SIZE, type AnnieConversationAvatar } from "./annieTurns";
import { chatWorkSteps, MIN_LISTED_WORK_STEPS } from "./annieWorkSteps";

const STATUS_LINE_TEST_ID = "annie-status-line";
const WORK_STEPS_TEST_ID = "annie-work-steps";
const THINKING_AVATAR_TEST_ID = "annie-thinking-avatar";

type Props = {
	avatar: AnnieConversationAvatar | null;
	/** The user's name for Annie. */
	assistantName: string;
	/** The reply already has content on screen, so its turn avatar is showing. */
	turnStarted: boolean;
	text: string;
	/** The in-flight turn's status lines so far, oldest first. */
	steps: readonly string[];
	activeStep: string | null;
};

type StepListProps = { steps: readonly string[]; activeStep: string | null; avatar: AnnieConversationAvatar | null; assistantName: string };

/** The live step list; with a design, her thinking face opens it and glances at the step she's on. */
const AnnieWorkStepList: FC<StepListProps> = ({ steps, activeStep, avatar, assistantName }) => {
	const { t } = useTranslation();
	const rootRef = useRef<HTMLDivElement>(null);
	const faceRef = useRef<HTMLDivElement>(null);
	const [gaze, setGaze] = useState<GazeDirection | undefined>(undefined);

	// biome-ignore lint/correctness/useExhaustiveDependencies: `steps`/`activeStep` are the re-measure triggers — they move the active row.
	useLayoutEffect(() => {
		const face = faceRef.current;
		const row = rootRef.current?.querySelector(CHAT_WORK_STEP_ACTIVE_SELECTOR);
		if (face === null || row === null || row === undefined) return;
		const rowRect = row.getBoundingClientRect();
		// She reads the start of the line (glyph and first words), not the middle of a wide row.
		setGaze(gazeToward(face.getBoundingClientRect(), { left: rowRect.left, top: rowRect.top, width: rowRect.height, height: rowRect.height }));
	}, [steps, activeStep, avatar]);

	return (
		<ChatWorkSteps
			mode={ChatWorkStepsMode.Live}
			steps={chatWorkSteps(steps, activeStep)}
			ariaLabel={t("annie.work_steps.aria_label", { name: assistantName })}
			rootRef={rootRef}
			leadingWidth={avatar === null ? undefined : ANNIE_TURN_AVATAR_SIZE}
			leading={
				avatar === null ? undefined : (
					<Box ref={faceRef} sx={{ display: "flex" }}>
						<AssistantAvatar
							config={avatar.config}
							mood={AssistantAvatarMood.Thinking}
							size={ANNIE_TURN_AVATAR_SIZE}
							animated
							gaze={gaze}
							dataTestId={THINKING_AVATAR_TEST_ID}
						/>
					</Box>
				)
			}
			dataTestId={WORK_STEPS_TEST_ID}
		/>
	);
};

/** Annie's in-flight status: one step is a single line; two or more are a live list, shown with or without a design. */
export const AnnieThinkingLine: FC<Props> = ({ avatar, assistantName, turnStarted, text, steps, activeStep }) => {
	if (steps.length >= MIN_LISTED_WORK_STEPS) {
		if (avatar !== null && turnStarted) {
			return (
				<ChatAvatarRow avatar={null} columnWidth={ANNIE_TURN_AVATAR_SIZE}>
					<AnnieWorkStepList steps={steps} activeStep={activeStep} avatar={null} assistantName={assistantName} />
				</ChatAvatarRow>
			);
		}
		return <AnnieWorkStepList steps={steps} activeStep={activeStep} avatar={avatar} assistantName={assistantName} />;
	}
	if (avatar === null) return <ChatThinkingIndicator dataTestId={STATUS_LINE_TEST_ID} text={text} />;
	if (turnStarted) {
		return (
			<ChatAvatarRow avatar={null} columnWidth={ANNIE_TURN_AVATAR_SIZE}>
				<ChatThinkingIndicator dataTestId={STATUS_LINE_TEST_ID} text={text} />
			</ChatAvatarRow>
		);
	}
	return (
		<ChatThinkingIndicator
			dataTestId={STATUS_LINE_TEST_ID}
			text={text}
			sx={{ px: 0 }}
			leading={
				<AssistantAvatar
					config={avatar.config}
					mood={AssistantAvatarMood.Thinking}
					size={ANNIE_TURN_AVATAR_SIZE}
					animated
					dataTestId={THINKING_AVATAR_TEST_ID}
				/>
			}
		/>
	);
};
